import React from "react";

  // SHIMMER_KEYFRAMES_INJECTED
  if (typeof document !== "undefined" && !document.getElementById("policy-shimmer-keyframes")) {
    const styleEl = document.createElement("style");
    styleEl.id = "policy-shimmer-keyframes";
    styleEl.textContent = "@keyframes policyShimmer { 100% { transform: translateX(100%); } }";
    document.head.appendChild(styleEl);
  }
  
const Shimmer = ({ className = "", style, ...rest }) => (
  <div
    className={`relative overflow-hidden bg-[#E5E9EB] rounded-md before:absolute before:inset-0 before:-translate-x-full before:animate-[policyShimmer_1.4s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/70 before:to-transparent ${className}`}
    style={style}
    aria-hidden="true"
    {...rest}
  />
);

const BenefitRowSkeleton = ({ width = "80%" }) => (
  <div className="flex items-start gap-3">
    <Shimmer className="w-4 h-4 rounded-full mt-1 flex-shrink-0" />
    <Shimmer className="h-3.5" style={{ width }} />
  </div>
);

const InfoRowSkeleton = () => (
  <div className="flex items-center justify-between py-3 border-b border-[#EEF1F3] last:border-b-0">
    <Shimmer className="h-3.5 w-24" />
    <Shimmer className="h-3.5 w-32" />
  </div>
);

const MotorPolicyDetailsSkeleton = ({ partialData }) => {
  const hasPartial = !!partialData;

  return (    <div className="p-6 max767:p-0" data-testid="motor-policy-skeleton" role="status" aria-label="Loading policy details">
      {/* Desktop breadcrumb */}
      <div className="max767:hidden mb-4">
        <div className="flex items-center gap-2">
          <Shimmer className="h-3 w-20" />
          <Shimmer className="h-3 w-3 rounded-full" />
          <Shimmer className="h-3 w-32" />
        </div>
      </div>

      {/* Mobile sticky top bar (matches MotorPolicyCard mobile header) */}
      <div className="hidden max767:flex items-center justify-between bg-[#EDFAFF] p-4 sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <Shimmer className="w-6 h-6 rounded-full" />
          <Shimmer className="h-4 w-24 rounded-3xl" />
        </div>
        <Shimmer className="h-4 w-20" />
      </div>

      {/* MotorPolicyCard skeleton — light blue rounded card */}
      <div className="bg-[#EDFAFF] rounded-2xl p-6 mb-3 mt-6 max767:mx-4 max767:mt-4 max767:p-5 max-600:rounded-none">
        <div className="flex items-start justify-between gap-4 max-600:flex-col">
          <div className="flex-1 min-w-0">
            {/* Plate number / vehicle title */}
            {hasPartial && (partialData?.RegNo || partialData?.PolicyNumber) ? (
              <p className="text-[#1A2024] text-xl font-semibold mb-2 truncate">
                {partialData.RegNo || partialData.PolicyNumber}
              </p>
            ) : (
              <Shimmer className="h-6 w-48 mb-3" />
            )}

            {/* Make / model */}
            {hasPartial && (partialData?.Make || partialData?.Model) ? (
              <p className="text-[#48535B] text-sm font-medium mb-4 truncate">
                {[partialData?.Make, partialData?.Model].filter(Boolean).join(" ")}
              </p>
            ) : (
              <Shimmer className="h-3 w-36 mb-4" />
            )}

            {/* From / To dates */}
            <div className="flex items-center gap-6 mt-4 max767:flex-col max767:items-start max767:gap-3">
              <div>
                <Shimmer className="h-3 w-16 mb-2" />
                <Shimmer className="h-4 w-24" />
              </div>
              <div>
                <Shimmer className="h-3 w-16 mb-2" />
                <Shimmer className="h-4 w-24" />
              </div>
            </div>
          </div>

          {/* Right side — download / renew button */}
          <div className="flex flex-col items-end gap-3 max-600:items-stretch max-600:w-full">
            <Shimmer className="h-9 w-40 rounded-md max767:hidden" />
            <Shimmer className="w-9 h-9 rounded-full max767:hidden" />
          </div>
        </div>
      </div>

      {/* Tabs strip skeleton */}
      <div className="flex items-center gap-6 border-b border-[#EEF1F3] mb-6 mt-4 max767:px-4 overflow-hidden">
        <div className="pb-3 border-b-2 border-[#005574]">
          <Shimmer className="h-4 w-28" />
        </div>
        <div className="pb-3">
          <Shimmer className="h-4 w-32 opacity-60" />
        </div>
        <div className="pb-3 max767:hidden">
          <Shimmer className="h-4 w-28 opacity-60" />
        </div>
        <div className="pb-3 max767:hidden">
          <Shimmer className="h-4 w-28 opacity-60" />
        </div>
      </div>

      {/* PolicyOverviewTab content skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 max767:px-4">
        {/* Left: plan / benefits */}
        <div className="lg:col-span-3 bg-white border border-[#DDE2E4] rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shimmer className="w-12 h-12 rounded-full" />
            <div className="flex flex-col gap-2">
              <Shimmer className="h-5 w-48" />
              <Shimmer className="h-3 w-32" />
            </div>
          </div>

          {/* Included Benefits header */}
          <div className="mb-4 mt-6">
            <Shimmer className="h-5 w-40" />
          </div>

          {/* Benefit rows with varied widths */}
          <div className="space-y-3">
            <BenefitRowSkeleton width="85%" />
            <BenefitRowSkeleton width="70%" />
            <BenefitRowSkeleton width="90%" />
            <BenefitRowSkeleton width="60%" />
            <BenefitRowSkeleton width="78%" />
          </div>
        </div>

        {/* Right: quick actions + documents */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* File a Claim quick action */}
          <div className="bg-[#F6F8F9] border border-[#DDE2E4] rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <Shimmer className="w-9 h-9 rounded-full" />
              <Shimmer className="h-5 w-32" />
            </div>
          </div>

          {/* Emergency / Assistance */}
          <div className="bg-[#F6F8F9] rounded-2xl p-6">
            <Shimmer className="h-5 w-40 mb-4" />
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div className="flex items-center gap-3">
                <Shimmer className="w-10 h-10 rounded-full" />
                <Shimmer className="h-4 w-24" />
              </div>
              <div className="flex items-center gap-3">
                <Shimmer className="w-10 h-10 rounded-full" />
                <Shimmer className="h-4 w-24" />
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-[#F6F8F9] rounded-2xl p-6">
            <Shimmer className="h-4 w-48 mb-4" />
            <div className="space-y-3">
              <InfoRowSkeleton />
              <InfoRowSkeleton />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MotorPolicyDetailsSkeleton;
